<script>
const STATUS_ATTRIBUTE_KEY = 'status';

function buildDefaultFilter() {
  return {
    attributeKey: STATUS_ATTRIBUTE_KEY,
    filterOperator: 'equal_to',
    values: [],
    queryOperator: 'or',
  };
}
</script>

<script setup>
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useOperators } from 'next/filter/operators';

import Button from 'next/button/Button.vue';
import ConditionRow from 'next/filter/ConditionRow.vue';

const emit = defineEmits(['applyFilter', 'close']);
const filters = defineModel({
  type: Array,
  default: () => [buildDefaultFilter()],
});

const { t } = useI18n();
const { equalityOperators } = useOperators();

const filterTypes = computed(() => [
  {
    attributeKey: STATUS_ATTRIBUTE_KEY,
    value: STATUS_ATTRIBUTE_KEY,
    attributeName: t('WGPT_SOCIAL_MEDIA_POSTS.FILTER.STATUS_ATTRIBUTE'),
    label: t('WGPT_SOCIAL_MEDIA_POSTS.FILTER.STATUS_ATTRIBUTE'),
    inputType: 'multiSelect',
    options: [
      { id: 'pending', name: t('WGPT_SOCIAL_MEDIA_POSTS.STATUS.PENDING') },
      {
        id: 'processing',
        name: t('WGPT_SOCIAL_MEDIA_POSTS.STATUS.PROCESSING'),
      },
      {
        id: 'published',
        name: t('WGPT_SOCIAL_MEDIA_POSTS.STATUS.PUBLISHED'),
      },
      { id: 'failed', name: t('WGPT_SOCIAL_MEDIA_POSTS.STATUS.FAILED') },
      {
        id: 'removal_requested',
        name: t('WGPT_SOCIAL_MEDIA_POSTS.STATUS.REMOVAL_REQUESTED'),
      },
      { id: 'removed', name: t('WGPT_SOCIAL_MEDIA_POSTS.STATUS.REMOVED') },
    ],
    dataType: 'text',
    filterOperators: equalityOperators.value,
    attributeModel: 'standard',
  },
]);

const conditionsRef = useTemplateRef('conditionsRef');

const resetFilter = () => {
  filters.value = [buildDefaultFilter()];
};

const clearFilter = () => {
  resetFilter();
  emit('applyFilter', filters.value);
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push(buildDefaultFilter());
};

const isConditionsValid = () =>
  conditionsRef.value.every(condition => condition.validate());

const applyFilter = () => {
  if (!isConditionsValid()) return;
  emit('applyFilter', filters.value);
};

const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleSocialMediaPostsFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 w-[32rem] max-w-[90vw] overflow-visible border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ t('WGPT_SOCIAL_MEDIA_POSTS.FILTER.TITLE') }}
    </h3>
    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="`filter-${index}`">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :show-query-operator="false"
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex justify-between gap-2">
      <Button sm ghost blue @click="addFilter">
        {{ t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="clearFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button sm solid blue @click="applyFilter">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
